/**
 * Suggested reply — an AI draft of the agent's next message, shown at the end
 * of the thread directly above the Reply / Forward buttons.
 *
 * Generates on its own as soon as the thread is open, so the agent sees a
 * ready draft rather than having to ask for one. Clicking it opens the
 * composer with the text already in place.
 *
 * Grounded in the real thread: `POST /agent/ai/conversations/:id/suggest-reply`
 * reads this conversation's customer-visible messages server-side. Internal
 * notes are excluded there, so a private note can never leak into text headed
 * for the customer.
 *
 * It only runs when the newest message is INBOUND. Drafting a reply to the
 * agent's own last message is nonsense, and auto-generating on every thread
 * open is a real model call — on local CPU inference, tens of seconds of one
 * core per conversation viewed. Gating on "a reply is actually expected"
 * keeps that cost proportional to the work.
 *
 * The draft is never sent. It populates the composer, where the agent edits
 * and sends through the normal path, keeping every approval and audit rule
 * intact.
 *
 * Translation is inline: when translated, the draft card shows the translated
 * text IN PLACE of the original, with a compact banner and "Show original"
 * toggle — never appended below.
 *
 * Styling matches the agent workspace: 3px corners, `#3366FF` accent.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, ChevronUp, Copy, Info, Languages, Loader2, MessageSquareText, RefreshCw, ShieldCheck, ShieldAlert, ShieldX, Sparkles, Square, X } from 'lucide-react'

import { useAiStream } from '../../../../hooks/useAiStream.js'
import { api } from '../../../../api/client.js'

const SUGGEST_LANGUAGES = [
  'English', 'Spanish', 'French', 'German', 'Italian', 'Portuguese', 'Dutch',
  'Russian', 'Arabic', 'Hindi', 'Bengali', 'Urdu', 'Turkish', 'Persian',
  'Chinese (Simplified)', 'Japanese', 'Korean', 'Vietnamese', 'Thai',
  'Indonesian', 'Malay', 'Filipino', 'Swahili', 'Hebrew', 'Polish',
  'Ukrainian', 'Czech', 'Greek', 'Romanian', 'Hungarian', 'Swedish',
  'Norwegian', 'Danish', 'Finnish', 'Catalan', 'Serbian', 'Croatian',
  'Bulgarian', 'Slovak', 'Slovenian', 'Latvian', 'Lithuanian', 'Estonian',
  'Tamil', 'Telugu', 'Marathi', 'Gujarati',
] as const

// Multilingual patterns for greetings (start of message)
const GREETING_PATTERN = /^(hi|hello|hey|dear|greetings|bonjour|salut|cher|chère|hola|estimado|estimada|querido|querida|hallo|liebe|lieber|sehr geehrte|ciao|gentile|buongiorno|olá|prezado|prezada|привет|здравствуйте|уважаемый|مرحبا|عزيزي|नमस्ते|प्रिय|你好|亲爱的|こんにちは|안녕하세요)/i

// Multilingual patterns for sign-offs (end of message)
const SIGNOFF_PATTERN = /(regards|sincerely|best|thanks|cheers|respectfully|cordialement|merci|bien à vous|salutations|saludos|atentamente|gracias|mit freundlichen grüßen|danke|beste grüße|cordiali saluti|grazie|distinti saluti|atenciosamente|obrigado|obrigada|с уважением|спасибо|شكرا|مع تحياتي|धन्यवाद|सादर|谢谢|此致敬礼|ありがとう|敬具|감사합니다)/i

// Multilingual patterns for empathy phrases
const EMPATHY_PATTERN = /\b(sorry|apolog|understand|unfortunate|frustrat|inconvenience|appreciate|we value|thank you|please let me know|happy to help|here to help|we hear you|we regret|désolé|excus|comprend|malheureus|désagrément|apprécions|nous sommes là|lo siento|disculp|comprend|lament|inconvenient|agradec|estamos aquí|entschuldig|versteh|bedauer|unannehmlich|wir schätzen|ci scusiamo|comprendiamo|spiacent|siamo qui|desculp|compreend|lament|inconveniência|agradec|estamos aqui|извин|понима|сожале|неудобств|ценим|نعتذر|نفهم|نأسف|क्षमा|समझ|असुविध|抱歉|理解|遗憾|不便|申し訳|ご理解|죄송|이해|불편)\b/gi

// Multilingual patterns for formal tone
const FORMAL_PATTERN = /\b(regards|sincerely|furthermore|however|therefore|pursuant|kindly|aforementioned|cordialement|veuillez|conformément|toutefois|atentamente|por favor|conforme|sin embargo|mit freundlichen grüßen|bitte|gemäß|jedoch|cordiali saluti|la prego|conformemente|tuttavia|atenciosamente|por favor|conforme|contudo|с уважением|пожалуйста|в соответствии|однако)\b/gi

// Multilingual patterns for friendly tone
const FRIENDLY_PATTERN = /\b(hey|hi there|happy to|glad to|love to|awesome|great to hear|sounds good|cheers|salut|super|génial|avec plaisir|hola|genial|estupendo|con gusto|hey|toll|super|gerne|ciao|fantastico|ottimo|con piacere|oi|legal|ótimo|com prazer|привет|отлично|супер|с удовольствием|مرحبا|رائع|ممتاز|बढ़िया|शानदार|太好了|很高兴|すごい|喜んで|좋아요|기꺼이)\b/gi

// Multilingual placeholder pattern
const PLACEHOLDER_PATTERN = /\[(?:customer|name|insert|your|client|nom|nombre|kunde|cliente|имя|اسم|नाम|名前|이름)[^\]]*\]/i

interface TranslateResult {
  text: string
  confidence: number | null
  grammar: number | null
}

export interface SuggestedReplyProps {
  conversationId: string
  /**
   * False when the newest message is outbound — no reply is expected, so no
   * draft is generated and nothing renders.
   */
  enabled: boolean
  /** Open the composer with this draft. */
  onUse: (text: string) => void
  /** Hide the card for the rest of this view. */
  onDismiss: () => void
}

export default function SuggestedReply({
  conversationId,
  enabled,
  onUse,
  onDismiss,
}: SuggestedReplyProps): JSX.Element | null {
  const { text, running, error, run, stop } = useAiStream()
  const [showLangDropdown, setShowLangDropdown] = useState(false)
  const [translating, setTranslating] = useState(false)
  const [translated, setTranslated] = useState<TranslateResult | null>(null)
  const [translateLang, setTranslateLang] = useState<string | null>(null)
  const [showOriginal, setShowOriginal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [copiedTranslate, setCopiedTranslate] = useState(false)
  const [langSearch, setLangSearch] = useState('')
  const translateCacheRef = useRef<Map<string, TranslateResult>>(new Map())
  const langBtnRef = useRef<HTMLButtonElement>(null)
  const [langDropdownPos, setLangDropdownPos] = useState<{ top: number; left: number } | null>(null)

  // One automatic generation per conversation. Without this guard, any parent
  // re-render that changes the effect's identity would fire another model
  // call — expensive and visibly restarts the draft under the agent.
  const generatedFor = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    if (generatedFor.current === conversationId) return
    generatedFor.current = conversationId
    void run(`/agent/ai/conversations/${conversationId}/suggest-reply`, { tone: 'neutral' })

    return () => {
      // Release the guard when the effect tears down.
      //
      // StrictMode mounts effects twice in development: the first mount starts
      // the request and sets the guard, the teardown aborts that request (via
      // useAiStream's own cleanup), and without this the second mount would
      // see the guard already set and skip — leaving no request in flight and
      // nothing rendered at all. The deps are stable, so this only runs on a
      // real unmount or a conversation change; it does not cause extra calls.
      generatedFor.current = null
    }
  }, [conversationId, enabled, run])

  const handleTranslate = useCallback(async (language: string): Promise<void> => {
    if (!text) return
    const cacheKey = `${language}:${text.slice(0, 100)}`
    const cached = translateCacheRef.current.get(cacheKey)
    if (cached) {
      setTranslated(cached)
      setTranslateLang(language)
      setShowOriginal(false)
      setShowLangDropdown(false)
      setLangSearch('')
      return
    }

    setTranslating(true)
    setTranslateLang(language)
    setShowOriginal(false)
    setShowLangDropdown(false)
    setLangSearch('')

    try {
      const res = await api.post<{ translatedText: string; confidence: number | null; grammar: number | null }>(
        '/agent/ai/translate',
        { text, targetLanguage: language },
      )
      const result: TranslateResult = { text: res.translatedText, confidence: res.confidence, grammar: res.grammar }
      translateCacheRef.current.set(cacheKey, result)
      setTranslated(result)
    } catch {
      setTranslated(null)
    } finally {
      setTranslating(false)
    }
  }, [text])

  const handleCopy = useCallback((): void => {
    if (!text) return
    void navigator.clipboard?.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }, [text])

  const handleCopyTranslated = useCallback((): void => {
    if (!translated?.text) return
    void navigator.clipboard?.writeText(translated.text)
    setCopiedTranslate(true)
    setTimeout(() => setCopiedTranslate(false), 2000)
  }, [translated?.text])

  const handleRegenerate = useCallback((): void => {
    generatedFor.current = null
    generatedFor.current = conversationId
    setTranslated(null)
    setTranslateLang(null)
    setShowOriginal(false)
    void run(`/agent/ai/conversations/${conversationId}/suggest-reply`, { tone: 'neutral' })
  }, [conversationId, run])

  const filteredLangs = useMemo(() => {
    if (!langSearch.trim()) return SUGGEST_LANGUAGES
    const q = langSearch.toLowerCase()
    return SUGGEST_LANGUAGES.filter((l) => l.toLowerCase().includes(q))
  }, [langSearch])

  // Compute a confidence score based on draft quality heuristics (multilingual)
  const confidenceScore = useMemo((): number | null => {
    if (!text || running) return null
    const activeText = (translated && !showOriginal) ? translated.text : text
    let score = 70
    if (activeText.length > 100) score += 10
    if (activeText.length > 250) score += 5
    if (GREETING_PATTERN.test(activeText.trim())) score += 5
    if (SIGNOFF_PATTERN.test(activeText)) score += 5
    if (!PLACEHOLDER_PATTERN.test(activeText)) score += 5
    if (activeText.length < 50) score -= 15
    const words = activeText.split(/\s+/)
    const unique = new Set(words.map((w) => w.toLowerCase()))
    if (words.length > 10 && unique.size / words.length < 0.6) score -= 10
    return Math.max(0, Math.min(100, score))
  }, [text, running, translated, showOriginal])

  // Human touch indicators (multilingual)
  const hasGreeting = useMemo(() => {
    const activeText = (translated && !showOriginal) ? translated.text : text
    return GREETING_PATTERN.test(activeText.trim())
  }, [text, translated, showOriginal])

  const hasSignOff = useMemo(() => {
    const activeText = (translated && !showOriginal) ? translated.text : text
    return SIGNOFF_PATTERN.test(activeText)
  }, [text, translated, showOriginal])

  const hasPersonalization = useMemo(() => {
    const activeText = (translated && !showOriginal) ? translated.text : text
    if (!activeText) return false
    return activeText.length > 80 && !PLACEHOLDER_PATTERN.test(activeText)
  }, [text, translated, showOriginal])

  // Empathy score — detects empathetic language that builds trust (multilingual)
  const empathyScore = useMemo((): number | null => {
    if (!text || running) return null
    const activeText = (translated && !showOriginal) ? translated.text : text
    const matches = activeText.match(EMPATHY_PATTERN)
    const count = matches ? matches.length : 0
    if (count === 0) return 30
    if (count === 1) return 65
    if (count === 2) return 85
    return 95
  }, [text, running, translated, showOriginal])

  // Clarity score — readability based on sentence length and word complexity
  const clarityScore = useMemo((): number | null => {
    if (!text || running) return null
    const activeText = (translated && !showOriginal) ? translated.text : text
    const sentences = activeText.split(/[.!?。！？]+/).filter((s) => s.trim().length > 0)
    if (sentences.length === 0) return null
    const words = activeText.split(/\s+/).filter(Boolean)
    const avgWordsPerSentence = words.length / sentences.length
    const longWords = words.filter((w) => w.length > 8).length
    const longWordRatio = words.length > 0 ? longWords / words.length : 0
    let score = 100
    if (avgWordsPerSentence > 25) score -= 20
    else if (avgWordsPerSentence > 20) score -= 10
    if (longWordRatio > 0.25) score -= 15
    else if (longWordRatio > 0.15) score -= 5
    if (sentences.length < 2) score -= 10
    return Math.max(0, Math.min(100, score))
  }, [text, running, translated, showOriginal])

  // Tone detection — shows the detected tone of the draft (multilingual)
  const detectedTone = useMemo((): 'Friendly' | 'Formal' | 'Concise' | 'Neutral' | null => {
    if (!text || running) return null
    const activeText = (translated && !showOriginal) ? translated.text : text
    const conciseMarkers = activeText.trim().length < 150
    const formalCount = (activeText.match(FORMAL_PATTERN) || []).length
    const friendlyCount = (activeText.match(FRIENDLY_PATTERN) || []).length
    if (friendlyCount > formalCount) return 'Friendly'
    if (formalCount > 1) return 'Formal'
    if (conciseMarkers) return 'Concise'
    return 'Neutral'
  }, [text, running, translated, showOriginal])

  // Overall Trust Score — weighted combination of all quality metrics
  const trustScore = useMemo((): { score: number; level: 'high' | 'medium' | 'low'; label: string; description: string } | null => {
    if (!text || running) return null
    if (confidenceScore == null || empathyScore == null || clarityScore == null) return null

    // Weighted average: confidence 40%, clarity 35%, empathy 25%
    const weighted = Math.round(
      confidenceScore * 0.4 +
      clarityScore * 0.35 +
      empathyScore * 0.25
    )

    // Bonus for human touch indicators
    let bonus = 0
    if (hasGreeting) bonus += 3
    if (hasSignOff) bonus += 3
    if (hasPersonalization) bonus += 4

    const finalScore = Math.min(100, weighted + bonus)

    if (finalScore >= 80) {
      return {
        score: finalScore,
        level: 'high',
        label: 'Ready to Use',
        description: 'This draft is well-structured and appropriate. Minor edits recommended.',
      }
    }
    if (finalScore >= 60) {
      return {
        score: finalScore,
        level: 'medium',
        label: 'Review Recommended',
        description: 'This draft needs review. Check tone and add personalization.',
      }
    }
    return {
      score: finalScore,
      level: 'low',
      label: 'Needs Editing',
      description: 'This draft requires significant editing before sending.',
    }
  }, [text, running, confidenceScore, empathyScore, clarityScore, hasGreeting, hasSignOff, hasPersonalization])

  // State for expanded trust details
  const [showTrustDetails, setShowTrustDetails] = useState(false)

  if (!enabled) return null
  if (!running && !text && !error) return null

  // Which text to show in the draft card: translated (if active and not showing original) or original
  const isTranslated = !!translated && !showOriginal
  const displayText = isTranslated ? translated!.text : text
  const displayCopied = isTranslated ? copiedTranslate : copied
  const handleDisplayCopy = isTranslated ? handleCopyTranslated : handleCopy

  return (
    <div className="mt-3">
      {/* Header row with Trust Score */}
      <div className="mb-2.5 flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600">
          <Sparkles className="h-3.5 w-3.5 text-[#3366FF]" />
          Suggested reply
        </span>

        {/* Trust Score Badge — prominent display when draft is complete */}
        {!running && text && trustScore && (
          <button
            type="button"
            onClick={() => setShowTrustDetails((v) => !v)}
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all hover:shadow-sm ${
              trustScore.level === 'high'
                ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200/60 hover:bg-emerald-100'
                : trustScore.level === 'medium'
                ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200/60 hover:bg-amber-100'
                : 'bg-red-50 text-red-700 ring-1 ring-red-200/60 hover:bg-red-100'
            }`}
            title={trustScore.description}
          >
            {trustScore.level === 'high' ? (
              <ShieldCheck className="h-3.5 w-3.5" />
            ) : trustScore.level === 'medium' ? (
              <ShieldAlert className="h-3.5 w-3.5" />
            ) : (
              <ShieldX className="h-3.5 w-3.5" />
            )}
            <span className="font-bold">{trustScore.score}</span>
            <span className="hidden sm:inline">• {trustScore.label}</span>
            {showTrustDetails ? (
              <ChevronUp className="h-3 w-3 opacity-60" />
            ) : (
              <ChevronDown className="h-3 w-3 opacity-60" />
            )}
          </button>
        )}

        {/* Quick indicators — compact badges */}
        {!running && text && (
          <div className="hidden items-center gap-1.5 sm:flex">
            {hasSignOff && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-1.5 py-[1px] text-[10px] font-medium text-blue-600" title="Includes a professional sign-off">
                <Check className="h-2.5 w-2.5" /> Sign-off
              </span>
            )}
            {hasPersonalization && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-purple-50 px-1.5 py-[1px] text-[10px] font-medium text-purple-600" title="No placeholders — ready to personalize">
                <Sparkles className="h-2.5 w-2.5" /> Personalized
              </span>
            )}
          </div>
        )}

        {running && (
          <button
            type="button"
            onClick={stop}
            className="ml-auto inline-flex items-center gap-1 text-[11px] text-slate-500 transition-colors hover:text-slate-800"
          >
            <Square className="h-3 w-3" /> Stop
          </button>
        )}

        {!running && (text || error) && (
          <div className="ml-auto flex items-center gap-1">
            {/* Copy button — copies whichever text is currently displayed */}
            {text && (
              <button
                type="button"
                onClick={handleDisplayCopy}
                className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-1 text-[11px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                title={isTranslated ? 'Copy translated text' : 'Copy suggested reply'}
              >
                {displayCopied ? <><Check className="h-3 w-3 text-emerald-600" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
              </button>
            )}

            {/* Translate button */}
            {text && !translated && (
              <button
                ref={langBtnRef}
                type="button"
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect()
                  setShowLangDropdown((v) => {
                    if (!v) {
                      setLangDropdownPos({ top: rect.bottom + 4, left: rect.left })
                    }
                    return !v
                  })
                  setLangSearch('')
                }}
                className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-1 text-[11px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                title="Translate suggested reply"
              >
                <Languages className="h-3 w-3" /> Translate
                <ChevronDown className={`h-2.5 w-2.5 transition-transform ${showLangDropdown ? 'rotate-180' : ''}`} />
              </button>
            )}

            {/* Show original / Show translation toggle */}
            {translated && (
              <button
                type="button"
                onClick={() => setShowOriginal((v) => !v)}
                className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-1 text-[11px] font-medium text-[#3366FF] transition-colors hover:bg-[#3366FF]/8"
                title={showOriginal ? 'Show translation' : 'Show original'}
              >
                {showOriginal ? 'Show translation' : 'Show original'}
              </button>
            )}

            <button
              type="button"
              onClick={handleRegenerate}
              className="inline-flex items-center gap-1 rounded-[3px] px-1.5 py-1 text-[11px] text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
              title="Generate a different draft"
            >
              <RefreshCw className="h-3 w-3" /> Regenerate
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="inline-flex h-6 w-6 items-center justify-center rounded-[3px] text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
              title="Dismiss"
              aria-label="Dismiss suggested reply"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        )}
      </div>

      {error ? (
        <div className="rounded-[3px] border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
          {error}
        </div>
      ) : (
        <>
          {/* Translation banner — shown inline above the draft card */}
          {(translating || (translated && !showOriginal)) && (
            <div className="mb-1.5 flex items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-1.5">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700">
                <Languages className="h-3 w-3 text-slate-500" />
                {translating
                  ? `Translating to ${translateLang}…`
                  : `Translated: → ${translateLang}`}
              </span>
              {translated && !translating && (
                <button
                  type="button"
                  onClick={() => setShowOriginal(true)}
                  className="text-[11px] font-medium text-[#3366FF] transition-colors hover:text-[#2952cc] hover:underline"
                >
                  Show original
                </button>
              )}
            </div>
          )}

          {/* Showing-original banner */}
          {translated && showOriginal && (
            <div className="mb-1.5 flex items-center justify-between gap-2 rounded-[4px] border border-slate-200 bg-slate-50/80 px-3 py-1.5">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-700">
                <Languages className="h-3 w-3 text-slate-500" />
                Showing original
              </span>
              <button
                type="button"
                onClick={() => setShowOriginal(false)}
                className="text-[11px] font-medium text-[#3366FF] transition-colors hover:text-[#2952cc] hover:underline"
              >
                Show translation
              </button>
            </div>
          )}

          {/* Trust Score Details Panel — expandable breakdown of all metrics */}
          {showTrustDetails && trustScore && !translating && (
            <div className={`mb-2 overflow-hidden rounded-[4px] border ${
              trustScore.level === 'high'
                ? 'border-emerald-200 bg-emerald-50/50'
                : trustScore.level === 'medium'
                ? 'border-amber-200 bg-amber-50/50'
                : 'border-red-200 bg-red-50/50'
            }`}>
              {/* Header with overall score */}
              <div className="flex items-center justify-between border-b border-inherit bg-white/60 px-3 py-2">
                <div className="flex items-center gap-2">
                  {trustScore.level === 'high' ? (
                    <ShieldCheck className="h-4 w-4 text-emerald-600" />
                  ) : trustScore.level === 'medium' ? (
                    <ShieldAlert className="h-4 w-4 text-amber-600" />
                  ) : (
                    <ShieldX className="h-4 w-4 text-red-600" />
                  )}
                  <span className="text-[12px] font-semibold text-slate-800">
                    Trust Score: {trustScore.score}/100
                  </span>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                  trustScore.level === 'high'
                    ? 'bg-emerald-100 text-emerald-700'
                    : trustScore.level === 'medium'
                    ? 'bg-amber-100 text-amber-700'
                    : 'bg-red-100 text-red-700'
                }`}>
                  {trustScore.label}
                </span>
              </div>

              {/* Description */}
              <div className="border-b border-inherit px-3 py-2">
                <p className="flex items-start gap-1.5 text-[11px] text-slate-600">
                  <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                  {trustScore.description}
                </p>
              </div>

              {/* Score breakdown */}
              <div className="grid grid-cols-3 gap-3 px-3 py-2.5">
                {/* Confidence */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-slate-500">Content Quality</span>
                    <span className={`text-[10px] font-bold ${
                      (confidenceScore ?? 0) >= 80 ? 'text-emerald-600' :
                      (confidenceScore ?? 0) >= 60 ? 'text-amber-600' : 'text-red-600'
                    }`}>{confidenceScore ?? 0}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (confidenceScore ?? 0) >= 80 ? 'bg-emerald-500' :
                        (confidenceScore ?? 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${confidenceScore ?? 0}%` }}
                    />
                  </div>
                </div>

                {/* Clarity */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-slate-500">Clarity</span>
                    <span className={`text-[10px] font-bold ${
                      (clarityScore ?? 0) >= 80 ? 'text-emerald-600' :
                      (clarityScore ?? 0) >= 60 ? 'text-amber-600' : 'text-red-600'
                    }`}>{clarityScore ?? 0}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (clarityScore ?? 0) >= 80 ? 'bg-emerald-500' :
                        (clarityScore ?? 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${clarityScore ?? 0}%` }}
                    />
                  </div>
                </div>

                {/* Empathy */}
                <div className="flex flex-col gap-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-slate-500">Empathy</span>
                    <span className={`text-[10px] font-bold ${
                      (empathyScore ?? 0) >= 80 ? 'text-emerald-600' :
                      (empathyScore ?? 0) >= 60 ? 'text-amber-600' : 'text-red-600'
                    }`}>{empathyScore ?? 0}%</span>
                  </div>
                  <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
                    <div
                      className={`h-full rounded-full transition-all ${
                        (empathyScore ?? 0) >= 80 ? 'bg-emerald-500' :
                        (empathyScore ?? 0) >= 60 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${empathyScore ?? 0}%` }}
                    />
                  </div>
                </div>
              </div>

              {/* Human touch indicators */}
              <div className="flex flex-wrap items-center gap-2 border-t border-inherit px-3 py-2">
                <span className="text-[10px] font-medium text-slate-500">Detected:</span>
                {hasGreeting && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                    <MessageSquareText className="h-2.5 w-2.5" /> Greeting
                  </span>
                )}
                {hasSignOff && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700">
                    <Check className="h-2.5 w-2.5" /> Sign-off
                  </span>
                )}
                {hasPersonalization && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium text-purple-700">
                    <Sparkles className="h-2.5 w-2.5" /> Personalized
                  </span>
                )}
                {detectedTone && (
                  <span className="inline-flex items-center gap-0.5 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                    {detectedTone} tone
                  </span>
                )}
                {!hasGreeting && !hasSignOff && !hasPersonalization && (
                  <span className="text-[10px] italic text-slate-400">No special markers detected</span>
                )}
              </div>
            </div>
          )}

          {/* Main draft card — shows translated OR original text in place */}
          {translating ? (
            <div className="w-full rounded-[3px] border border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <Loader2 className="h-3 w-3 animate-spin text-[#3366FF]" />
                Translating to {translateLang}…
              </div>
              <div className="mt-2.5 space-y-2">
                <div className="h-3 w-full animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3 w-[90%] animate-pulse rounded-[3px] bg-slate-200/70" />
                <div className="h-3 w-[75%] animate-pulse rounded-[3px] bg-slate-200/70" />
              </div>
            </div>
          ) : (
          <button
            type="button"
            onClick={() => text && !running && onUse(text)}
            disabled={running || !text}
            className="w-full rounded-[3px] border border-slate-200 bg-white px-4 py-3 text-left transition-all enabled:hover:border-[#3366FF]/50 enabled:hover:bg-[#3366FF]/[0.02] enabled:hover:shadow-sm disabled:cursor-default"
            title={running ? 'Still drafting…' : 'Click to use this reply'}
          >
            <span className="block whitespace-pre-wrap break-words text-[13px] leading-relaxed text-slate-500">
              {displayText || 'Drafting a reply from this conversation…'}
              {running && (
                <span className="ml-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-[#3366FF] align-text-bottom" />
              )}
            </span>
            {!running && text && !translating && (
              <span className="mt-2 block text-[11px] text-[#3366FF]">Click to use this reply</span>
            )}
          </button>
          )}

          {/* Trust note — reminds agent this is AI-drafted and needs human review */}
          {!running && text && !translating && (
            <div className="mt-1.5 flex items-center justify-between px-1">
              <div className="flex items-center gap-1.5 text-[10px] text-slate-400">
                <Sparkles className="h-2.5 w-2.5 text-[#3366FF]/60" />
                AI draft — review and edit before sending.
              </div>
              {trustScore && !showTrustDetails && (
                <button
                  type="button"
                  onClick={() => setShowTrustDetails(true)}
                  className="text-[10px] font-medium text-[#3366FF] hover:underline"
                >
                  View trust details
                </button>
              )}
            </div>
          )}

          {/* Score bars — shown below draft card when translated */}
          {isTranslated && translated && (translated.confidence != null || translated.grammar != null) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-4 px-1">
              {translated.confidence != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-slate-500">Confidence</span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${translated.confidence >= 80 ? 'bg-emerald-500' : translated.confidence >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${translated.confidence}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-semibold ${translated.confidence >= 80 ? 'text-emerald-700' : translated.confidence >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                    {translated.confidence}%
                  </span>
                </div>
              )}
              {translated.grammar != null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-slate-500">Grammar</span>
                  <div className="h-1.5 w-16 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${translated.grammar >= 80 ? 'bg-emerald-500' : translated.grammar >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                      style={{ width: `${translated.grammar}%` }}
                    />
                  </div>
                  <span className={`text-[10px] font-semibold ${translated.grammar >= 80 ? 'text-emerald-700' : translated.grammar >= 60 ? 'text-amber-700' : 'text-red-700'}`}>
                    {translated.grammar}%
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Language dropdown — rendered via portal to escape overflow-hidden ancestors */}
          {showLangDropdown && langDropdownPos && createPortal(
            <div className="fixed z-[99999] w-56 border border-slate-200 bg-white shadow-xl" style={{ borderRadius: '4px', top: langDropdownPos.top, left: langDropdownPos.left }}>
              <div className="border-b border-slate-100 p-2">
                <input
                  type="text"
                  value={langSearch}
                  onChange={(e) => setLangSearch(e.target.value)}
                  placeholder="Search language…"
                  className="w-full rounded-[4px] border border-slate-200 px-2 py-1.5 text-xs text-slate-700 outline-none focus:border-[#3366FF]"
                  autoFocus
                />
              </div>
              <div className="max-h-48 overflow-y-auto py-1">
                {filteredLangs.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400">No language found</div>
                ) : (
                  filteredLangs.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => void handleTranslate(lang)}
                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-slate-700 hover:bg-blue-50 hover:text-blue-700"
                    >
                      {lang}
                    </button>
                  ))
                )}
              </div>
            </div>,
            document.body
          )}
        </>
      )}
    </div>
  )
}
